import { Auth0Client } from "@auth0/auth0-spa-js";
import { t } from "../i18n/index.js";
import type { AuthResult, PlatformAdapter } from "./PlatformAdapter.js";

// Standalone web login (Facebook/Google/email) — Auth0 Universal Login. See
// CLAUDE.md "Rešen auth za standalone web (Auth0)" for the full design.
// `useRefreshTokens`/`cacheLocation: "localstorage"` matter here specifically
// because Auth0's default silent-refresh (a hidden iframe) depends on
// third-party cookies, which Safari/Brave block outright — without this, a
// returning player would silently get logged out on token expiry instead of
// refreshing.
const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN as string | undefined;
const AUTH0_CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID as string | undefined;
const AUTH0_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE as string | undefined;

function createAuth0Client(): Auth0Client {
  if (!AUTH0_DOMAIN || !AUTH0_CLIENT_ID || !AUTH0_AUDIENCE) {
    throw new Error("VITE_AUTH0_DOMAIN/VITE_AUTH0_CLIENT_ID/VITE_AUTH0_AUDIENCE must be set — see apps/web/.env.example");
  }
  return new Auth0Client({
    domain: AUTH0_DOMAIN,
    clientId: AUTH0_CLIENT_ID,
    authorizationParams: {
      redirect_uri: window.location.origin,
      audience: AUTH0_AUDIENCE,
      scope: "openid profile email",
    },
    useRefreshTokens: true,
    cacheLocation: "localstorage",
  });
}

let auth0Client: Auth0Client | null = null;
function getAuth0Client(): Auth0Client {
  auth0Client ??= createAuth0Client();
  return auth0Client;
}

// Dev-only bypass for manually testing the real browser UI without an Auth0
// tenant configured (`apps/web/.env`'s VITE_AUTH0_* — see .env.example).
// Mirrors what apps/web/scripts/smoke-test.mjs already does at the protocol
// level via the server's POST /auth/dev-token (only mounted when the server
// has DEV_AUTH_ENABLED=true — never in production, see CLAUDE.md), just
// wired into the actual login screen instead of a headless script. Open
// `?dev=<any-name>` once; it's remembered in localStorage so a reload or a
// second tab on the same origin doesn't need the param again. `import.meta.env.DEV`
// means `vite build` strips this whole branch from production bundles —
// even a stray `?dev=` on the deployed site can't do anything, since the
// code path plus the server route are both gone/unmounted there.
const DEV_ID_KEY = "meksiko.devId";

function getDevId(): string | null {
  if (!import.meta.env.DEV) return null;
  return localStorage.getItem(DEV_ID_KEY);
}

// Google AdSense H5 Games Ads (Ad Placement API) — the browser-native
// equivalent of AdMob for a page-hosted HTML5 game; AdMob itself is
// Android/iOS-only and has no web SDK. Requires a separate AdSense account
// (not an AdMob one) enrolled in the H5 Games Ads program:
// https://adsense.google.com/start/h5-games-ads/
// Until VITE_ADSENSE_CLIENT_ID is set (and the account is approved), this
// silently falls back to the local countdown demo below, so the coin-reward
// flow stays testable end-to-end without a live account.
const ADSENSE_CLIENT_ID = import.meta.env.VITE_ADSENSE_CLIENT_ID as string | undefined;
// Test mode shows mock ads (real timing/behavior, no real impressions or
// revenue) — see https://developers.google.com/ad-placement/docs/test.
// Defaults to on for `vite dev`, off for a production build, unless
// VITE_ADSENSE_TEST_MODE explicitly overrides it (e.g. to test-mode a
// production build before the AdSense account is approved).
const ADSENSE_TEST_MODE = (import.meta.env.VITE_ADSENSE_TEST_MODE as string | undefined) ?? String(import.meta.env.DEV);

// No @types package exists for the Ad Placement API (it's a plain
// window-global `adsbygoogle.push()` queue) — shapes below are from Google's
// own docs (developers.google.com/ad-placement/apis/adbreak).
interface AdBreakPlacementInfo {
  breakStatus:
    | "viewed"
    | "dismissed"
    | "notReady"
    | "timeout"
    | "invalid"
    | "error"
    | "noAdPreloaded"
    | "frequencyCapped"
    | "ignored"
    | "other";
}
interface AdBreakOptions {
  type: "reward";
  name?: string;
  beforeReward?: (showAdFn: () => void) => void;
  // Required for type "reward" — the runtime validates the placement config
  // and rejects the whole adBreak() call (logging "Invalid placement config:
  // missing required properties 'adDismissed', 'adViewed'" and never
  // prompting an ad) if either is absent, despite older docs listing them as
  // optional. Reproduced live with a real, approved AdSense account.
  adDismissed: () => void;
  adViewed: () => void;
  adBreakDone?: (info: AdBreakPlacementInfo) => void;
}
declare global {
  interface Window {
    adBreak?: (options: AdBreakOptions) => void;
    adsbygoogle?: unknown[];
  }
}

// Resolves true only if the real script actually loaded — window.adBreak
// gets assigned synchronously below regardless (it's just a queue shim), so
// checking that alone can't tell a live SDK apart from one that 404s/503s
// (e.g. an unapproved AdSense account, or an ad blocker): callers must check
// this boolean, or adBreak() calls silently queue forever and never resolve.
let adsenseReady: Promise<boolean> | null = null;

/** Injects the Ad Placement API script once; resolves once it's known whether adBreak() will actually do anything. */
function loadAdsenseScript(): Promise<boolean> {
  if (!ADSENSE_CLIENT_ID) return Promise.resolve(false);
  if (adsenseReady) return adsenseReady;

  adsenseReady = new Promise((resolve) => {
    // Google's documented shim: adsbygoogle.push() queues calls made before
    // the async script tag below finishes loading, so adBreak() is safe to
    // call immediately after this runs.
    window.adsbygoogle = window.adsbygoogle ?? [];
    window.adBreak = (options) => window.adsbygoogle!.push(options);

    const script = document.createElement("script");
    script.async = true;
    script.crossOrigin = "anonymous";
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT_ID}`;
    if (ADSENSE_TEST_MODE === "true") script.dataset.adbreakTest = "on";
    script.addEventListener("load", () => resolve(true));
    script.addEventListener("error", () => resolve(false)); // showRewardedAd() falls back to the demo modal
    document.head.appendChild(script);
  });
  return adsenseReady;
}

/**
 * Starts loading the ad script only once the player is signed in — never on
 * the loading/login screens. AdSense policy forbids Google-served ads on
 * screens without publisher content (login, loading, alerts), and its site
 * review flagged exactly that while the script was loaded from init().
 * Fire-and-forget: common ad blockers block googlesyndication.com outright,
 * and a stalled request must not hold up sign-in. showRewardedAd() awaits
 * the same cached promise later, by which point it's almost always settled.
 */
function preloadAdsAfterLogin(): void {
  void loadAdsenseScript();
}

/**
 * Standalone web adapter — used for local dev/testing and any non-Facebook
 * distribution (GAME_SPEC.md §13: the same frontend must run standalone).
 *
 * showRewardedAd() uses Google AdSense H5 Games Ads when VITE_ADSENSE_CLIENT_ID
 * is set, falling back to a local countdown demo otherwise (no account
 * configured yet, or the account isn't approved — see the H5 Games Ads note
 * above). The economy flow around it (server-issued ticket, redeemed only
 * after a minimum watch time — see apps/server/src/economy/adSessions.ts) is
 * real and already wired regardless of which path shows the ad.
 */
export class WebPlatformAdapter implements PlatformAdapter {
  readonly kind = "web" as const;

  async init(): Promise<void> {
    if (import.meta.env.DEV) {
      const params = new URLSearchParams(location.search);
      const devId = params.get("dev");
      if (devId) {
        localStorage.setItem(DEV_ID_KEY, devId);
        window.history.replaceState({}, "", location.pathname);
      }
    }

    // Auth0 bounces back here with ?code=...&state=... after a successful
    // (or failed — ?error=...) Universal Login redirect. Must run before
    // isAuthenticated()/authenticate() below so the SDK's session cache is
    // populated from it.
    const params = new URLSearchParams(location.search);
    if (params.has("code") && params.has("state")) {
      try {
        await getAuth0Client().handleRedirectCallback();
      } catch {
        // Login failed or was cancelled — isAuthenticated() below will
        // correctly report false and the login screen shows again.
      }
      window.history.replaceState({}, "", location.pathname);
    }
  }

  async isAuthenticated(): Promise<boolean> {
    if (getDevId()) return true;
    return getAuth0Client().isAuthenticated();
  }

  login(connection?: "facebook" | "google-oauth2"): void {
    void getAuth0Client().loginWithRedirect(connection ? { authorizationParams: { connection } } : {});
  }

  async logout(): Promise<void> {
    if (getDevId()) {
      localStorage.removeItem(DEV_ID_KEY);
      return;
    }
    // openUrl: false clears the SDK's local cache (localStorage) only —
    // no redirect to Auth0's /v2/logout endpoint, which would otherwise
    // navigate away right after a "your account was deleted" confirmation.
    await getAuth0Client().logout({ openUrl: false });
  }

  async authenticate(apiBaseUrl: string): Promise<AuthResult> {
    const devId = getDevId();
    if (devId) {
      const res = await fetch(`${apiBaseUrl}/api/auth/dev-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ devId }),
      });
      if (res.ok) {
        preloadAdsAfterLogin();
        return res.json();
      }
      // Bypass unavailable (route not mounted — DEV_AUTH_ENABLED unset)
      // — forget it and fall through to the real Auth0 flow below.
      localStorage.removeItem(DEV_ID_KEY);
    }

    const auth0 = getAuth0Client();
    const accessToken = await auth0.getTokenSilently();
    const claims = await auth0.getIdTokenClaims();
    const displayName = (claims?.name as string | undefined) ?? (claims?.nickname as string | undefined) ?? "Igrač";
    // Google/Facebook social connections put the account's profile photo URL
    // here; email/password signups get Auth0's default gravatar-style image.
    const photoUrl = (claims?.picture as string | undefined) ?? null;

    const res = await fetch(`${apiBaseUrl}/api/auth/sync`, {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
      body: JSON.stringify({ displayName, photoUrl }),
    });
    if (!res.ok) throw new Error("Auth sync failed");
    const synced = await res.json();
    preloadAdsAfterLogin();
    return {
      token: accessToken,
      userId: synced.userId,
      displayName: synced.displayName,
      photoUrl: synced.photoUrl ?? null,
      coinBalance: synced.coinBalance,
    };
  }

  async showRewardedAd(): Promise<boolean> {
    const ready = await loadAdsenseScript(); // cached after init(); resolves immediately here
    if (!ready || !window.adBreak) return this.showPlaceholderAd();

    return new Promise((resolve) => {
      // Safety net: adBreakDone is documented to always fire, but this SDK
      // won't see real traffic until the AdSense account is approved, and an
      // unapproved/misconfigured account can leave adBreak() hanging with no
      // callback at all (reproduced locally against an invalid client ID) —
      // without this, the "watch ad" button would look permanently dead.
      // 30s, not 10s: a real/mock rewarded ad's own countdown routinely runs
      // ~10s, so a 10s safety net raced it and won almost every time —
      // resolving false right as the real adViewed() was about to fire,
      // silently losing the reward with no error (reproduced locally).
      // Mirrors facebook.ts's showRewardedAd() console.warn on failure — not
      // shown to the player (see watchAd() in appStore.ts for that), just
      // visible in support/bug reports. adBreakDone's breakStatus (notReady,
      // error, frequencyCapped, ignored, ...) is more specific than anything
      // FBInstant exposes, so log it verbatim rather than guessing a reason.
      const timeout = setTimeout(() => {
        console.warn("[Meksiko] showRewardedAd timed out after 30s (adBreakDone never fired — possibly blocked)");
        resolve(false);
      }, 30_000);
      window.adBreak!({
        type: "reward",
        name: "coin_reward",
        // Our own UI already gates this call behind an explicit "watch ad"
        // button, so no extra confirmation prompt is needed here — trigger
        // the ad as soon as one is available.
        beforeReward: (showAdFn) => showAdFn(),
        // adDismissed/adViewed only fire when a suitable ad was actually
        // found and shown — adBreakDone below is the safety net for every
        // other case (notReady, error, frequency-capped, ...).
        adDismissed: () => {
          clearTimeout(timeout);
          resolve(false);
        },
        adViewed: () => {
          clearTimeout(timeout);
          resolve(true);
        },
        // Always fires last, regardless of outcome. Only needed here for
        // statuses other than "viewed"/"dismissed" (no ad available, error,
        // frequency cap, ...) — those cases have no adDismissed/adViewed call.
        adBreakDone: (info) => {
          clearTimeout(timeout);
          if (info.breakStatus !== "viewed" && info.breakStatus !== "dismissed") {
            console.warn("[Meksiko] showRewardedAd adBreakDone:", info.breakStatus);
            resolve(false);
          }
        },
      });
    });
  }

  private showPlaceholderAd(): Promise<boolean> {
    return new Promise((resolve) => {
      const overlay = document.createElement("div");
      overlay.className = "ad-overlay";
      overlay.innerHTML = `
        <div class="ad-modal">
          <p>${t("ad.placeholderText")}</p>
          <p class="ad-countdown">15</p>
        </div>
      `;
      document.body.appendChild(overlay);
      let remaining = 15;
      const countdownEl = overlay.querySelector(".ad-countdown")!;
      const interval = setInterval(() => {
        remaining -= 1;
        countdownEl.textContent = String(remaining);
        if (remaining <= 0) {
          clearInterval(interval);
          overlay.remove();
          resolve(true);
        }
      }, 1000);
    });
  }

  async shareInvite(): Promise<void> {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Meksiko", text: t("share.inviteText"), url: location.href });
      } catch {
        // user cancelled — fine
      }
    }
  }

  renderPlayerPortrait(): void {
    // No overlay API on standalone web — UI falls back to initials/seat labels.
  }
}
